/** PLAYERS — venue badges, one player per phone, and each venue's own public
 *  join link. One box at the top of the Goal Game control page: every switch
 *  here goes off again in one tap from this same box, and off returns the
 *  behaviour the game had before badges existed.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../lib/api';
import { Copy, QrCode, Search } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';

const API = '/goal-game/admin/players';

const Row = ({ title, hint, on, onChange, testid }) => (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5">
        <div className="min-w-0">
            <p className="text-sm font-medium">{title}</p>
            <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <Switch checked={on} onCheckedChange={onChange} data-testid={testid} />
    </div>
);

export const GoalPlayersBlock = ({ organizationId }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [qr, setQr] = useState('');
    const [query, setQuery] = useState('');
    const [player, setPlayer] = useState(undefined);

    const load = useCallback(async () => {
        const res = await api.get(`${API}?organization_id=${organizationId || ''}`);
        setData(res.data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const post = async (path, body) => {
        setBusy(true);
        try {
            const res = await api.post(`${API}${path}`,
                { organization_id: organizationId, ...body });
            if (res.data?.player !== undefined) setPlayer(res.data.player);
            else setData(res.data);
        } finally { setBusy(false); }
    };

    const find = async () => {
        setBusy(true);
        try {
            const res = await api.get(
                `${API}/find?organization_id=${organizationId || ''}&q=${encodeURIComponent(query)}`);
            setPlayer(res.data.player);
        } finally { setBusy(false); }
    };

    if (!data) return <p className="text-sm text-muted-foreground">…</p>;

    const fullLink = (path) => `${window.location.origin}${path}`;

    return (
        <div className="space-y-4" data-testid="gg-players-block">
            <Row title={t('goalGame.badgesSwitch')} hint={t('goalGame.badgesSwitchHint')}
                 on={data.badges_enabled} testid="gg-players-badges-switch"
                 onChange={(v) => post('/settings', { badges_enabled: v })} />
            <Row title={t('goalGame.onePhoneSwitch')} hint={t('goalGame.onePhoneSwitchHint')}
                 on={data.one_phone_enabled} testid="gg-players-phone-switch"
                 onChange={(v) => post('/settings', { one_phone_enabled: v })} />

            <div className="space-y-2">
                {(data.venues || []).length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="gg-players-no-venues">
                        No venues on this organization yet.
                    </p>
                )}
                {(data.venues || []).map((v) => (
                    <div key={v.id} className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5"
                         data-testid={`gg-players-venue-${v.id}`}>
                        <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{v.name}</p>
                                <p className="text-xs text-muted-foreground">
                                    {t('goalGame.venueLink')} · {t('goalGame.joinedFromLink')}:{' '}
                                    <span className="tabular-nums">{v.joined?.today || 0}</span> {t('goalGame.joinedToday')} ·{' '}
                                    <span className="tabular-nums">{v.joined?.all_time || 0}</span> {t('goalGame.joinedAllTime')}
                                </p>
                            </div>
                            <Switch checked={v.link_enabled} disabled={busy}
                                    data-testid={`gg-players-link-switch-${v.id}`}
                                    onCheckedChange={(on) => post('/link', { venue_id: v.id, on })} />
                        </div>
                        {v.link_enabled && v.join_path && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <code className="text-xs text-muted-foreground break-all">{fullLink(v.join_path)}</code>
                                <Button size="sm" variant="outline" data-testid={`gg-players-copy-${v.id}`}
                                        onClick={() => navigator.clipboard?.writeText(fullLink(v.join_path))}>
                                    <Copy size={13} className="mr-1" />{t('goalGame.copyLink')}
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`gg-players-qr-${v.id}`}
                                        onClick={() => setQr(qr === v.id ? '' : v.id)}>
                                    <QrCode size={13} className="mr-1" />{t('goalGame.showQr')}
                                </Button>
                            </div>
                        )}
                        {qr === v.id && v.join_path && (
                            <img alt="" data-testid={`gg-players-qr-img-${v.id}`} className="mt-2 h-40 w-40"
                                 src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(fullLink(v.join_path))}`} />
                        )}
                    </div>
                ))}
            </div>

            {/* One player at a time: their badges, tonight's venue and the phone. */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5 space-y-2">
                <p className="text-sm font-medium">{t('goalGame.playerTools')}</p>
                <div className="flex items-center gap-2">
                    <Input value={query} onChange={(e) => setQuery(e.target.value)}
                           placeholder={t('goalGame.playerToolsHint')} data-testid="gg-players-find-input"
                           onKeyDown={(e) => e.key === 'Enter' && find()} />
                    <Button size="sm" onClick={find} disabled={busy || !query.trim()}
                            data-testid="gg-players-find-button">
                        <Search size={14} />
                    </Button>
                </div>
                {player === null && (
                    <p className="text-xs text-muted-foreground" data-testid="gg-players-none">No player with that email or screen name.</p>
                )}
                {player && (
                    <div className="space-y-2 text-sm" data-testid="gg-players-found">
                        <p className="font-medium">{player.screen_name} · {player.email}</p>
                        <p>
                            <span className="text-muted-foreground">{t('goalGame.badgesLabel')}: </span>
                            {(player.badges || []).length === 0 ? '—' : player.badges.map((b) => (
                                <span key={b.venue_id} className="mr-2 inline-flex items-center gap-1">
                                    {b.venue_name} ({b.how === 'link' ? t('goalGame.howLink') : t('goalGame.howCard')})
                                    <button type="button" className="text-xs text-red-600 hover:underline"
                                            data-testid={`gg-players-remove-${b.venue_id}`} disabled={busy}
                                            onClick={() => post('/edit', { email: player.email, venue_id: b.venue_id, action: 'remove' })}>
                                        {t('goalGame.removeBadge')}
                                    </button>
                                </span>
                            ))}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            {(data.venues || [])
                                .filter((v) => !(player.badges || []).some((b) => b.venue_id === v.id))
                                .map((v) => (
                                    <Button key={v.id} size="sm" variant="outline" disabled={busy}
                                            data-testid={`gg-players-add-${v.id}`}
                                            onClick={() => post('/edit', { email: player.email, venue_id: v.id, action: 'add' })}>
                                        {t('goalGame.addBadge')}: {v.name}
                                    </Button>
                                ))}
                        </div>
                        <p>
                            <span className="text-muted-foreground">{t('goalGame.playingTonight')}: </span>
                            {player.tonight?.venue_name || '—'}
                            {player.tonight && (
                                <Button size="sm" variant="outline" className="ml-2" disabled={busy}
                                        data-testid="gg-players-unlock"
                                        onClick={() => post('/edit', { email: player.email, action: 'unlock' })}>
                                    {t('goalGame.unlockVenue')}
                                </Button>
                            )}
                        </p>
                        <p>
                            <span className="text-muted-foreground">{t('goalGame.phoneBound')}: </span>
                            {player.phone_bound ? 'yes' : 'no'}
                            {player.phone_bound && (
                                <Button size="sm" variant="outline" className="ml-2" disabled={busy}
                                        data-testid="gg-players-release"
                                        onClick={() => post('/edit', { email: player.email, action: 'release' })}>
                                    {t('goalGame.releasePhone')}
                                </Button>
                            )}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GoalPlayersBlock;
